import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import type {
  DisplayColumnSetting,
  RestaurantOverviewRow,
  RestaurantOverviewSnapshot,
  RestaurantOverviewSortKey,
  RestaurantSortOrder,
} from '@/models/restaurant-manager'
import { restaurantOverviewSortKeys, getRestaurantOverviewColumnLabelKey } from '@/models/restaurant-manager'
import { RestaurantCellRenderer } from './restaurant-cell-renderer'
import {
  restaurantRowHighlightStyle,
  restaurantTableCellStyle,
  restaurantTableColumnWidths,
  restaurantTableHeaderCellStyle,
  restaurantTableLineCellStyle,
  restaurantTableStyle,
  restaurantTableRowStyle,
  restaurantTableWrapperStyle,
} from './restaurant-table-styles'
import { RestaurantSortableColumnHeader } from './restaurant-sortable-column-header'

type Props = {
  snapshot: RestaurantOverviewSnapshot
  date: string
  sortKey?: RestaurantOverviewSortKey
  sortOrder?: RestaurantSortOrder
  onChangeSort: (sortKey: RestaurantOverviewSortKey, sortOrder: RestaurantSortOrder) => void
}

export const RestaurantTableSection: React.FC<Props> = ({ snapshot, date, sortKey, sortOrder, onChangeSort }) => {
  const visibleColumns = useMemo(
    () => snapshot.displayColumns.filter(column => column.isVisible).sort((left, right) => left.displayOrder - right.displayOrder),
    [snapshot.displayColumns],
  )

  const gridTemplateColumns = useMemo(() => {
    if (visibleColumns.length === 0) {
      return 'minmax(0, 1fr)'
    }

    return visibleColumns.map(column => restaurantTableColumnWidths[column.columnKey]).join(' ')
  }, [visibleColumns])

  // 固定列の left 値：isFixed な列の幅を累積して各列の sticky left を算出する
  const fixedColumnLeftMap = useMemo(() => {
    const map = new Map<DisplayColumnSetting['columnKey'], number>()
    let accumulated = 0
    for (const column of visibleColumns) {
      if (!column.isFixed) break
      map.set(column.columnKey, accumulated)
      accumulated += parseInt(restaurantTableColumnWidths[column.columnKey], 10)
    }
    return map
  }, [visibleColumns])

  if (snapshot.rows.length === 0) {
    return null
  }

  return (
    <div css={restaurantTableWrapperStyle}>
      <div css={[restaurantTableStyle, { '.header, .row': { gridTemplateColumns } }]}>
        <div className="header">
          {visibleColumns.map(column => (
            <div key={column.columnKey} css={[restaurantTableHeaderCellStyle, fixedCellStyle(fixedColumnLeftMap, column)]}>
              <ColumnHeader column={column} sortKey={sortKey} sortOrder={sortOrder} onChangeSort={onChangeSort} />
            </div>
          ))}
        </div>
        <div className="body">
          {snapshot.rows.map(row => (
            <div
              key={row.reservationId}
              className="row"
              css={[restaurantTableRowStyle, row.rowHighlighted && restaurantRowHighlightStyle, rowTrackStyle(row.subjects.length)]}
            >
              {visibleColumns.map(column => (
                <div
                  key={column.columnKey}
                  css={[
                    restaurantTableCellStyle,
                    isLineColumn(column.columnKey) ? restaurantTableLineCellStyle : undefined,
                    editableCellStyle(row, column.columnKey),
                    fixedCellStyle(fixedColumnLeftMap, column),
                  ]}
                >
                  <RestaurantCellRenderer row={row} columnKey={column.columnKey} date={date} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const ColumnHeader: React.FC<{
  column: DisplayColumnSetting
  sortKey?: RestaurantOverviewSortKey
  sortOrder?: RestaurantSortOrder
  onChangeSort: (sortKey: RestaurantOverviewSortKey, sortOrder: RestaurantSortOrder) => void
}> = ({ column, sortKey, sortOrder, onChangeSort }) => {
  const { t } = useTranslation()
  const label = t(getRestaurantOverviewColumnLabelKey(column.columnKey))

  if (!restaurantOverviewSortKeys.includes(column.columnKey as RestaurantOverviewSortKey)) {
    return <div>{label}</div>
  }

  return (
    <RestaurantSortableColumnHeader
      label={label}
      columnKey={column.columnKey as RestaurantOverviewSortKey}
      sortKey={sortKey}
      sortOrder={sortOrder}
      onChangeSort={onChangeSort}
    />
  )
}

const isEditableColumn = (row: RestaurantOverviewRow, columnKey: DisplayColumnSetting['columnKey']) => {
  switch (columnKey) {
    case 'mealNote':
    case 'spaceReservationDetails':
      return true
    case 'customerTags':
      return row.customerId != null
    default:
      return false
  }
}

// 科目名・単価・数量は1予約あたりn行を表示するため、行トラックを .row に定義し
// subgrid で共有することで、同じレコードを示す行の高さを列間で揃える
const lineColumnKeys: ReadonlyArray<DisplayColumnSetting['columnKey']> = ['subjectName', 'unitPrice', 'quantity', 'nights']

const isLineColumn = (columnKey: DisplayColumnSetting['columnKey']) => lineColumnKeys.includes(columnKey)

const rowTrackStyle = (lineCount: number) => css({ gridTemplateRows: `repeat(${Math.max(lineCount, 1)}, auto)` })

const editableCellStyle = (row: RestaurantOverviewRow, columnKey: DisplayColumnSetting['columnKey']) =>
  isEditableColumn(row, columnKey)
    ? css({
        '&:hover': { background: '#F5F5F5' },
        '&:hover .restaurant-editable-cell__button': { opacity: 1, pointerEvents: 'auto' },
      })
    : undefined

// isFixed な列を横スクロール中も左端に固定する
const fixedCellStyle = (fixedColumnLeftMap: Map<DisplayColumnSetting['columnKey'], number>, column: DisplayColumnSetting) => {
  const left = fixedColumnLeftMap.get(column.columnKey)
  if (left === undefined) return undefined

  const fixedKeys = Array.from(fixedColumnLeftMap.keys())
  const isLastFixed = fixedKeys.at(-1) === column.columnKey

  return css({
    position: 'sticky',
    left,
    zIndex: 1,
    // 親要素（.row / .header）の背景色を引き継ぎ、スクロール時に後ろのセルが透けないようにする
    background: 'inherit',
    ...(isLastFixed ? { borderRight: '2px solid #F2F2F2' } : {}),
  })
}
